import ImportCommissionAPI from "./services/ImportCommissionAPI";
import UploadCommissionFileResponse from "./model/UploadCommissionFileResponse";
import ConvertCommissionFileResponse from "./model/ConvertCommissionFileResponse";
import MigrateCommissionFileResponse from "./model/MigrateCommissionFileResponse";
import * as constant from '../../shared/util/constant'

class ImportCommissionStatementService {
	
	importCommissionAPI: ImportCommissionAPI = new ImportCommissionAPI();

	private readonly validExtensions = ['.xlsx'];
	private readonly maxFileSize = 1 * 1024 * 1024 * 1024;

	validateCarrier = (value: string): string => {
		if (!value) return constant.FIELD_BLANK
		return ''
	}

	validateMonth = (value: string): string => {
		if (!value) return constant.FIELD_BLANK
		return ''
	}

	validateFile = (file: File | null): string => {
		if (!file) return constant.CommissionStatementValidation.FILE_REQUIRED
		if (!this.validExtensions.some(ext => file.name.endsWith(ext))) {
			return constant.CommissionStatementValidation.FILE_INVALID_TYPE
		}
		if (file.size > this.maxFileSize) return constant.CommissionStatementValidation.FILE_SIZE_EXCEEDED
		return ''
	}
	
	uploadCommissionFile = async (
        file: File,
        payload: { fileName: string; carrierName: string; statementMonth: string }
    ): Promise<UploadCommissionFileResponse> => {
        return await this.importCommissionAPI.uploadCommissionFile({
            file,
            fileName: payload.fileName,
            carrierName: payload.carrierName,
            statementMonth: payload.statementMonth,
        });
    }

	processCommissionFile = async (fileId: string, carrierName: string): Promise<ConvertCommissionFileResponse> => {
        return await this.importCommissionAPI.processCommissionFile({ fileId, carrierName });
    }

    migrateCommissionFile = async (fileId: string, carrierName: string): Promise<MigrateCommissionFileResponse> => {
        return await this.importCommissionAPI.migrateCommissionFile({ fileId, carrierName });
    }

	// Placeholder code for multipart chunk uploading
    // Use this when Commission Statement file size increases more than 100 MB.
	CHUNK_SIZE = 10 * 1024 * 1024;
	
	multipartChunking = async (
		file: File,
		payload: {
			fileName: string;
			contentType: string;
			carrierName: string;
			statementMonth: string;
		}
	): Promise<any> => {
		try {
				const session = await this.importCommissionAPI.initiateUpload();
				
				const chunks = this.splitFileIntoChunks(file);
				
				for (let i = 0; i < chunks.length; i++) {
					const chunkResponse = await this.importCommissionAPI.uploadChunk(
						session.uploadSessionId,
						chunks[i],
					);
					
					if (!chunkResponse.success) {
						throw new Error(`Chunk ${i + 1} upload failed: ${chunkResponse.message}`);
					}
				}
				
				await this.importCommissionAPI.completeUpload({
					uploadSessionId: session.uploadSessionId,
					fileName: payload.fileName
				});
				
				return;
			} catch (error: any) {
				throw error;
			}
		}
	
		private splitFileIntoChunks = (file: File): Blob[] => {
			const chunks: Blob[] = [];
			let offset = 0;
			
			while (offset < file.size) {
				const chunkEnd = Math.min(offset + this.CHUNK_SIZE, file.size);
				const chunk = file.slice(offset, chunkEnd);
				chunks.push(chunk);
				offset = chunkEnd;
			}
			return chunks;
		}
}

export default ImportCommissionStatementService;